import { BILLING_PAYMENT_METHODS_QUERY_KEY } from '@/lib/query/constants/subscription-keys-constants';
import { useAuthenticatedQuery } from '@/hooks/auth/use-auth-query-hooks';
import { useAccountState } from '@/contexts/account/account-context';
import { SubscriptionsApiService } from '@/services/internal-api/subscriptions/subscriptions-api-service';
import type { StripePaymentMethod } from '@/services/internal-api/subscriptions/types/subscription-api-service-types';

interface UseBillingPaymentMethodsOptions {
  enabled?: boolean;
}

export function useBillingPaymentMethods(options?: UseBillingPaymentMethodsOptions) {
  const { currentAccount } = useAccountState();
  const subscription = currentAccount?.subscription;
  const subscriptionId = currentAccount?.subscriptionId ?? subscription?.id;
  const callerEnabled = options?.enabled ?? true;

  return useAuthenticatedQuery<StripePaymentMethod[], Error>(
    [BILLING_PAYMENT_METHODS_QUERY_KEY, subscriptionId],
    async () => {
      if (!subscriptionId) {
        throw new Error('Subscription ID is required');
      }
      const response = await SubscriptionsApiService.getStripePaymentMethods(subscriptionId);
      // API returns array directly in response.data, not wrapped in { paymentMethods: [...] }
      const paymentMethods = Array.isArray(response.data)
        ? response.data
        : (response.data?.paymentMethods ?? []);
      return paymentMethods;
    },
    {
      enabled: !!subscriptionId && callerEnabled,
      useErrorBoundary: false,
      staleTime: 30 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
}
